// src/pages/settings/settings/UserEmailConfiguration.tsx
import React, { useState, useEffect } from "react";
import { Button, Input, InputNumber, Select, Space, Tooltip, Modal, Divider, Typography, Alert, Form } from "antd";
import { PlusOutlined, SaveOutlined, MailOutlined, EditOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";
import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import DetailDrawer from "../../../components/shared/DetailDrawer";

import { userEmailConfigApi } from "../../../api/communications/emailConfigApi";
import { UserEmailConfig, SMTP_PROVIDERS, SMTPProvider } from "../../../types/communications/emailConfig";
import { validateEmailConfigForm, getConfigStatus, applyProviderPreset, sortConfigsByStatus } from "../../../utils/emailConfigUtils";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";
import type { UserUuid } from "../../../types/identity";

const { Option } = Select;
const { Text } = Typography;
const { confirm } = Modal;

interface UserEmailConfigurationProps {
	userId: UserUuid;
}

const UserEmailConfiguration: React.FC<UserEmailConfigurationProps> = ({ userId }) => {
	const [configs, setConfigs] = useState<UserEmailConfig[]>([]);
	const [loading, setLoading] = useState(false);
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [editingConfig, setEditingConfig] = useState<UserEmailConfig | null>(null);
	const [testDrawerVisible, setTestDrawerVisible] = useState(false);
	const [testingConfig, setTestingConfig] = useState<UserEmailConfig | null>(null);
	const [testEmail, setTestEmail] = useState("");
	const [testLoading, setTestLoading] = useState(false);

	const [form] = Form.useForm();
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadConfigurations();
	}, [userId]);

	const loadConfigurations = async () => {
		setLoading(true);
		try {
			const data = await userEmailConfigApi.getConfigurations(userId);
			setConfigs(sortConfigsByStatus(data) as UserEmailConfig[]);
		} catch (error) {
			showError("Failed to load email configurations");
		} finally {
			setLoading(false);
		}
	};

	const openDrawer = (config?: UserEmailConfig) => {
		if (config) {
			setEditingConfig(config);
			form.setFieldsValue({
				name: config.name,
				host: config.host,
				port: config.port,
				encryption: config.encryption,
				username: config.username,
				password: "",
				from_address: config.from_address,
				from_name: config.from_name,
			});
		} else {
			setEditingConfig(null);
			form.resetFields();
		}
		setDrawerVisible(true);
	};

	const closeDrawer = () => {
		setDrawerVisible(false);
		setEditingConfig(null);
		form.resetFields();
	};

	const handleProviderSelect = (provider: SMTPProvider) => {
		const updatedFormData = applyProviderPreset(provider, form.getFieldsValue());
		form.setFieldsValue(updatedFormData);
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			const validation = validateEmailConfigForm(values);

			if (!validation.isValid) {
				Object.entries(validation.errors).forEach(([field, error]) => {
					form.setFields([{ name: field, errors: [error] }]);
				});
				return;
			}

			setLoading(true);

			if (editingConfig) {
				await userEmailConfigApi.updateConfiguration(editingConfig.id, values);
				showSuccess("Email configuration updated successfully");
			} else {
				await userEmailConfigApi.createConfiguration({
					...values,
					user_id: userId,
				});
				showSuccess("Email configuration created successfully");
			}

			await loadConfigurations();
			closeDrawer();
		} catch (error) {
			showError(extractValidationErrors(error).join(" "));
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (config: UserEmailConfig) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete the configuration "${config.name}"?`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await userEmailConfigApi.deleteConfiguration(config.id);
					showSuccess("Configuration deleted successfully");
					await loadConfigurations();
				} catch (error) {
					showError("Failed to delete configuration");
				}
			},
		});
	};

	const handleActivate = async (config: UserEmailConfig) => {
		try {
			await userEmailConfigApi.activateConfiguration(config.id);
			showSuccess("Configuration activated successfully");
			await loadConfigurations();
		} catch (error) {
			showError("Failed to activate configuration");
		}
	};

	const handleTest = (config: UserEmailConfig) => {
		setTestingConfig(config);
		setTestEmail("");
		setTestDrawerVisible(true);
	};

	const executeTest = async () => {
		if (!testingConfig || !testEmail) return;

		setTestLoading(true);
		try {
			const result = await userEmailConfigApi.testConfiguration(testingConfig.id, {
				test_email: testEmail,
			});

			if (result.success) {
				showSuccess("Test email sent successfully!");
			} else {
				showError(result.message || "Test failed");
			}
			setTestDrawerVisible(false);
		} catch (error) {
			showError(extractValidationErrors(error).join(" "));
		} finally {
			setTestLoading(false);
		}
	};

	const columns = [
		{ title: "Name", dataIndex: "name", key: "name" },
		{ title: "SMTP Host", dataIndex: "host", key: "host" },
		{ title: "Port", dataIndex: "port", key: "port", width: 80 },
		{
			title: "Encryption",
			dataIndex: "encryption",
			key: "encryption",
			width: 100,
			render: (text: string) => text.toUpperCase(),
		},
		{ title: "From Address", dataIndex: "from_address", key: "from_address" },
		{
			title: "Status",
			key: "status",
			width: 130,
			render: (_: any, config: UserEmailConfig) => {
				const { status, statusText } = getConfigStatus(config);
				return <StatusTag status={status} label={statusText} />;
			},
		},
		{
			title: "",
			key: "actions",
			width: 110,
			render: (_: any, config: UserEmailConfig) => {
				const { status } = getConfigStatus(config);
				return (
					<Space size={2}>
						<Tooltip title="Test Configuration">
							<Button type="text" icon={<MailOutlined />} size="small" disabled={!config.is_verified} onClick={(e) => { e.stopPropagation(); handleTest(config); }} />
						</Tooltip>
						<Tooltip title="Edit">
							<Button type="text" icon={<EditOutlined />} size="small" onClick={(e) => { e.stopPropagation(); openDrawer(config); }} />
						</Tooltip>
						{status !== "active" && config.is_verified && (
							<Tooltip title="Activate">
								<Button type="text" icon={<CheckOutlined />} size="small" onClick={(e) => { e.stopPropagation(); handleActivate(config); }} />
							</Tooltip>
						)}
						<Tooltip title="Delete">
							<Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={(e) => { e.stopPropagation(); handleDelete(config); }} />
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return (
		<SectionCard
			title="Personal Email Configurations"
			subtitle="Configure your personal SMTP settings for sending emails like test orders"
			extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer()}>Add Configuration</Button>}
		>
			<Alert
				message="Personal email configurations allow you to send emails using your own email account instead of the system default."
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
			/>

			<DataTable
				columns={columns}
				dataSource={configs}
				rowKey="id"
				loading={loading && !drawerVisible}
				pagination={false}
				emptyTitle="No email configurations"
				emptyDescription="Add a personal SMTP configuration to send emails using your own account"
				emptyAction={{ label: "Add Configuration", onClick: () => openDrawer() }}
				bare
			/>

			{/* Configuration Drawer */}
			<DetailDrawer
				title={editingConfig ? "Edit Email Configuration" : "Add Email Configuration"}
				open={drawerVisible}
				onClose={closeDrawer}
				width="70vw"
				headerActions={
					<Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit} loading={loading}>
						{editingConfig ? "Update" : "Create"}
					</Button>
				}
			>
				<Form form={form} layout="vertical">
					{/* SMTP Provider Presets */}
					<div style={{ marginBottom: 16 }}>
						<Text strong style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
							Quick Setup
						</Text>
						<Space size="small" wrap>
							{SMTP_PROVIDERS.map((provider) => (
								<Button key={provider.name} size="small" onClick={() => handleProviderSelect(provider)}>
									{provider.name}
								</Button>
							))}
						</Space>
						<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
							For Gmail and Yahoo, use App Passwords instead of your regular password
						</Text>
					</div>

					<Divider style={{ margin: "12px 0" }} />

					<Form.Item label="Configuration Name" name="name" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="e.g., My Gmail Account" />
					</Form.Item>

					<Form.Item label="SMTP Host" name="host" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="smtp.gmail.com" />
					</Form.Item>

					<Form.Item label="Port" name="port" rules={[{ required: true, message: "Required" }]}>
						<InputNumber min={1} max={65535} style={{ width: "100%" }} />
					</Form.Item>

					<Form.Item label="Encryption" name="encryption" rules={[{ required: true, message: "Required" }]}>
						<Select>
							<Option value="tls">TLS (Recommended)</Option>
							<Option value="ssl">SSL</Option>
						</Select>
					</Form.Item>

					<Form.Item label="Username" name="username" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="your.email@gmail.com" />
					</Form.Item>

					<Form.Item label="Password" name="password" rules={editingConfig ? [] : [{ required: true, message: "Required" }]}>
						<Input.Password placeholder={editingConfig ? "Leave empty to keep current password" : "Your password or app password"} />
					</Form.Item>
					{editingConfig && (
						<Text type="secondary" style={{ fontSize: 11, marginTop: -16, display: "block", marginBottom: 16 }}>
							Leave empty to keep the current password
						</Text>
					)}

					<Form.Item label="From Address" name="from_address" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="your.email@gmail.com" />
					</Form.Item>

					<Form.Item label="From Name" name="from_name" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="Your Name" />
					</Form.Item>
				</Form>
			</DetailDrawer>

			{/* Test Drawer */}
			<DetailDrawer
				title="Test Email Configuration"
				open={testDrawerVisible}
				onClose={() => setTestDrawerVisible(false)}
				width={480}
				headerActions={
					<Button type="primary" onClick={executeTest} loading={testLoading} disabled={!testEmail}>
						Send Test
					</Button>
				}
			>
				<div style={{ marginBottom: 16 }}>
					<Alert message={`Testing configuration: ${testingConfig?.name}`} type="info" showIcon />
				</div>
				<div>
					<Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
						Test Email Address
					</Text>
					<Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" type="email" />
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
						A test email will be sent to this address
					</Text>
				</div>
			</DetailDrawer>
		</SectionCard>
	);
};

export default UserEmailConfiguration;
